import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';

import { UserService } from '@ppg/auth';
import { CurrentSiteState } from '@ppg/configuration';
import { ClientSiteSettingsKeys, PricingType } from '@ppg/core/enums';
import { LanguagesService } from '@ppg/core/language';
import { FeatureFlagService } from '@ppg/shared/feature-flag';

import { B2BProductPricingVariables } from '../models/b2b-product-pricing-variables.model';
import { B2CProductPricingVariables } from '../models/b2c-product-pricing-variables.model';
import { ProductPrice, ProductTintingFeePrice } from '../models/product-price.model';
import { TintingFeePricing } from '../models/product-pricing-service-items.model';
import { ProductPricing } from '../models/product-pricing.model';
import { B2BProductPricingGQL } from '../queries/b2b-product-pricing.query';
import { B2CProductPricingGQL } from '../queries/b2c-product-pricing.query';
import { getContextClientNameIfAnonymousTransactable } from '../utils/get-context-client-name.util';

@Injectable({
  providedIn: 'root',
})
export class PricingService {
  #b2bProductPricingGQL = inject(B2BProductPricingGQL);
  #b2cProductPricingGQL = inject(B2CProductPricingGQL);
  #userService = inject(UserService);
  #currentSiteState = inject(CurrentSiteState);
  #featureFlagService = inject(FeatureFlagService);
  #languageService = inject(LanguagesService);

  #isProductPricingGross = computed(
    () =>
      this.#currentSiteState.getSiteSettingValueByKey(ClientSiteSettingsKeys.ProductJourneyProductDetailsPricingType) ==
      PricingType.GROSS,
  );

  readonly isProductDetailsAnonymous = this.#currentSiteState.getSiteFlag(
    ClientSiteSettingsKeys.AnonymousJourneyProductDetailsAnonymousEnabled,
  );

  readonly globalSettings = toSignal(
    this.#featureFlagService.getGlobalSettingsAsAnonymous(this.#languageService.language()),
  );

  readonly contextClientName = computed(() =>
    getContextClientNameIfAnonymousTransactable(this.globalSettings(), this.isProductDetailsAnonymous),
  );

  getProductPrices(
    productPricingVariables: B2BProductPricingVariables | B2CProductPricingVariables,
    includeTintingFee: boolean = false,
  ) {
    const isAuthenticated = Boolean(this.#userService.user());
    const isB2BUser = this.#userService.isB2BUser();

    return isAuthenticated && isB2BUser
      ? this.#b2bProductPricingGQL
          .fetch(productPricingVariables as B2BProductPricingVariables, { fetchPolicy: 'no-cache' })
          .pipe(map((response) => this.#mapToProductPrice(response.data?.customerProductPrice, includeTintingFee)))
      : this.#b2cProductPricingGQL
          .fetch(productPricingVariables as B2CProductPricingVariables, {
            fetchPolicy: 'no-cache',
            ...this.contextClientName(),
          })
          .pipe(map((response) => this.#mapToProductPrice(response.data?.myPrice, includeTintingFee)));
  }

  #mapToProductPrice(model: ProductPricing, includeTintingFee: boolean): ProductPrice {
    const productPrice = this.#getProductPrice(model);
    const tintingFeePrice = this.#getTintingFeePrice(model.pricingServiceItems?.tintingFee ?? null, includeTintingFee);

    return {
      totalPrice: (productPrice.totalPrice ?? 0) + (tintingFeePrice.totalPrice ?? 0),
      pricePerUnit: (productPrice.pricePerUnit ?? 0) + (tintingFeePrice.pricePerUnit ?? 0),
      discountedUnitPrice: (productPrice.discountedUnitPrice ?? 0) + (tintingFeePrice.pricePerUnit ?? 0),
      pricePerPricingUnit: productPrice.pricePerPricingUnit,
      pricingUnitOfMeasure: productPrice.pricingUnitOfMeasure,
      omnibusPrice: productPrice.omnibusPrice,
      isHasPromotionEffects: productPrice.isHasPromotionEffects,
      currency: productPrice.currency,
    };
  }

  #getProductPrice(model: ProductPricing): ProductPrice {
    const isHasPromotionEffects = model.promotionEffects?.length > 0;

    return this.#isProductPricingGross()
      ? {
          totalPrice: model.totalGrossPrice,
          pricePerUnit: model.grossPricePerUnit,
          pricePerPricingUnit: model.grossPricePerPricingUnit,
          pricingUnitOfMeasure: model.pricingUnitOfMeasure,
          discountedUnitPrice: model.discountedUnitGrossPrice,
          omnibusPrice: model.omnibusPrice,
          currency: model.currencyCode,
          isHasPromotionEffects,
        }
      : {
          totalPrice: model.totalNetPrice,
          pricePerUnit: model.netPricePerUnit,
          pricePerPricingUnit: model.netPricePerPricingUnit,
          pricingUnitOfMeasure: model.pricingUnitOfMeasure,
          discountedUnitPrice: model.discountedUnitPrice,
          omnibusPrice: model.omnibusPrice,
          currency: model.currencyCode,
          isHasPromotionEffects,
        };
  }

  #getTintingFeePrice(tintingFee: TintingFeePricing | null, includeTintingFee: boolean): ProductTintingFeePrice {
    if (!includeTintingFee || !tintingFee) {
      return {
        totalPrice: 0,
        pricePerUnit: 0,
      };
    }

    return this.#isProductPricingGross()
      ? {
          totalPrice: tintingFee.totalGrossPrice,
          pricePerUnit: tintingFee.grossPricePerUnit,
        }
      : {
          totalPrice: tintingFee.totalNetPrice,
          pricePerUnit: tintingFee.netPricePerUnit,
        };
  }
}
